import { useQuery } from "@apollo/client";
import { VideoCameraIcon } from "@heroicons/react/24/outline";
import {
  ArrowPathIcon,
  UserIcon,
  VideoCameraIcon as VideoCameraSolidIcon,
} from "@heroicons/react/24/solid";
import { Button } from "@mui/material";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import StatCard from "../../components/StatCard/StatCard";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  VideoChatSession,
  VideoChatStatus as VideoChatStatusData,
  VideoChatUser,
} from "../../graphql/graphql";
import { VIDEO_CHAT_STATUS } from "../../graphql/queries-gql";

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const SessionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 12px 18px;
  border-bottom: 1px solid ${(props) => props.theme.border};
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover};
  }
`;

const Pair = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  min-width: 0;
`;

const PairIcon = styled.span`
  display: inline-flex;
  color: ${(props) => props.theme.colors.green};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const UserAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const AvatarFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.theme.surfaceActive};
  color: ${(props) => props.theme.secondaryTextColor};
`;

const UserText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const UserId = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 11.5px;
  color: ${(props) => props.theme.mutedTextColor};
`;

const Duration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const DurationLabel = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const DurationValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.colors.green};
`;

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

const SessionUser: React.FC<{ user: VideoChatUser }> = ({ user }) => (
  <UserInfo>
    {user.photoThumbnailUrl ? (
      <UserAvatar src={user.photoThumbnailUrl} alt={user.username} />
    ) : (
      <AvatarFallback>
        <UserIcon width={18} />
      </AvatarFallback>
    )}
    <UserText>
      <UserName>{user.username}</UserName>
      <UserId>ID: {user._id}</UserId>
    </UserText>
  </UserInfo>
);

const VideoChatStatus = () => {
  const { loading, error, data, refetch } = useQuery(VIDEO_CHAT_STATUS, {
    pollInterval: 5000,
  });

  const videoChatStatus: VideoChatStatusData | undefined =
    data?.videoChatStatus;
  if (data) {
    console.log("Video Chat Status Data:", videoChatStatus);
  }

  const sessions: VideoChatSession[] = videoChatStatus?.activeSessions ?? [];

  return (
    <Page
      title="Video chat status"
      description="Live view, refreshes every 5 seconds."
      actions={
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<ArrowPathIcon width={15} />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      }
    >
      {loading && !data ? (
        <Row justifyContent="center">
          <Loader />
        </Row>
      ) : error ? (
        <ErrorAlert error={error} />
      ) : (
        <>
          <SummaryGrid>
            <StatCard
              label="Active searchers"
              value={videoChatStatus?.activeSearchersCount || 0}
            />
            <StatCard
              label="Active sessions"
              value={videoChatStatus?.activeSessionsCount || 0}
            />
          </SummaryGrid>

          {sessions.length > 0 ? (
            <Panel
              title="Active video chat sessions"
              actions={<Badge $tone="green">{sessions.length} live</Badge>}
              padding={0}
            >
              {sessions.map((session, index) => (
                <SessionRow key={index}>
                  <Pair>
                    <SessionUser user={session.user1} />
                    <PairIcon>
                      <VideoCameraSolidIcon width={18} />
                    </PairIcon>
                    <SessionUser user={session.user2} />
                  </Pair>
                  <Duration>
                    <DurationLabel>Duration</DurationLabel>
                    <DurationValue>
                      {formatDuration(session.sessionDurationSeconds)}
                    </DurationValue>
                  </Duration>
                </SessionRow>
              ))}
            </Panel>
          ) : (
            <EmptyState
              Icon={VideoCameraIcon}
              title="No active video chat sessions"
            />
          )}
        </>
      )}
    </Page>
  );
};

export default VideoChatStatus;
